/**
 * DesgloseMonedaPopover — SSOT para explicar un total consolidado multi-moneda.
 *
 * POR QUE EXISTE: cuando la red mezcla monedas, un total tiene dos problemas que
 * el número solo no resuelve. Si se pudo convertir, el `≈` avisa que es
 * aproximado pero no dice con qué tasa ni cuánto puso cada moneda. Si NO se pudo,
 * el hueco `— !` dice "no confíes" pero deja al operador sin ninguna cifra, aunque
 * el dato nativo por moneda SÍ existe y es exacto.
 *
 * QUE HACE: al pulsar el `≈` (o el hueco), abre un panel con el desglose que emite
 * el backend en `moneda_meta.desglose`: cuánto hay en CADA moneda en su unidad
 * nativa, la tasa aplicada y de qué día es. El total consolidado pasa a ser
 * auditable a simple vista — cualquiera puede rehacer la cuenta.
 *
 * Los montos que muestra son NATIVOS, nunca convertidos: son el hecho estampado.
 * Una moneda sin tasa se marca explícitamente, porque es la razón de que el total
 * esté incompleto.
 *
 * API:
 *   DesgloseMonedaPopover.enlazar(elemento, { meta, campo });
 *     elemento → el nodo que muestra el total (se vuelve pulsable)
 *     meta     → `moneda_meta` del endpoint ({moneda, desglose, tasa_faltante, ...})
 *     campo    → clave del monto dentro de `desglose[].montos` (ej. 'jugado_total')
 *
 *   DesgloseMonedaPopover.marcaHtml(meta) → el `≈`/`— !` ya pulsable, para
 *     construir la celda desde un template string.
 */

import { formatearCentavosConSimbolo } from '../../../shared/services/moneda/moneda-formateador.js';
import { t } from '../services/i18n-admin.js';
import { hayDesgloseUtil, motivoDelTotal, lineaDeTasa, marcaDelTotal } from './desglose-moneda-logica.js';

const CLASE_DISPARADOR = 'desglose-moneda-disparador';
const ID_PANEL = 'desgloseMonedaPanel';
const COLOR_AVISO = 'var(--accent-orange, #f59e0b)';

function esc(texto) {
    return String(texto ?? '').replace(
        /[&<>"']/g,
        c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );
}

function _cerrarPanel() {
    const abierto = document.getElementById(ID_PANEL);
    if (abierto) abierto.remove();
}

function _filasHtml(meta, campo) {
    const monedaDestino = meta.moneda || '';
    return meta.desglose
        .map(entrada => {
            // `campo` puede ser una clave o VARIAS: un KPI del panel suele ser la suma de
            // más de un campo del backend ("Ingresado" = efectivo + pasarela). Si se pasa
            // una sola clave que no existe, la fila salía en guion aunque el dinero
            // estuviera ahí bajo otro nombre.
            const claves = Array.isArray(campo) ? campo : [campo];
            let monto;
            for (const clave of claves) {
                const valor = entrada.montos ? entrada.montos[clave] : undefined;
                if (valor === null || valor === undefined) continue;
                monto = (monto || 0) + valor;
            }
            const montoTexto =
                monto === null || monto === undefined
                    ? '—'
                    : formatearCentavosConSimbolo(monto, entrada.moneda);
            const sinTasa = entrada.tasa === null || entrada.tasa === undefined;
            return `
                <tr>
                    <td style="padding:4px 10px 4px 0;font-weight:600;white-space:nowrap;">${esc(entrada.moneda)}</td>
                    <td style="padding:4px 10px 4px 0;text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap;">${esc(montoTexto)}</td>
                    <td style="padding:4px 0;color:${sinTasa ? COLOR_AVISO : 'var(--text-secondary)'};font-size:11px;white-space:nowrap;">
                        ${esc(lineaDeTasa(entrada.moneda, monedaDestino, entrada.tasa, t))}
                    </td>
                </tr>`;
        })
        .join('');
}

function _abrirPanel(disparador, meta, campo) {
    _cerrarPanel();

    const panel = document.createElement('div');
    panel.id = ID_PANEL;
    panel.setAttribute('role', 'dialog');
    panel.style.cssText = [
        'position:absolute',
        'z-index:9999',
        'background:var(--bg-elevated, #1c1f26)',
        'border:1px solid var(--border, #333)',
        'border-radius:8px',
        'padding:10px 12px',
        'box-shadow:0 8px 24px rgba(0,0,0,.45)',
        'font-size:12px',
        'max-width:min(92vw, 340px)',
    ].join(';');

    const fechaTasa = meta.fecha_tasa ? ` · ${esc(meta.fecha_tasa)}` : '';
    panel.innerHTML = `
        <div style="font-weight:600;margin-bottom:6px;">${esc(t('Desglose por moneda'))}</div>
        <table style="border-collapse:collapse;width:100%;">${_filasHtml(meta, campo)}</table>
        <div style="margin-top:8px;padding-top:6px;border-top:1px solid var(--border, #333);color:var(--text-secondary);font-size:11px;">
            ${esc(motivoDelTotal(meta, t))}${fechaTasa}
        </div>`;

    document.body.appendChild(panel);

    // Posicionado bajo el disparador y corregido si se sale de la ventana: en
    // tablets el panel de admin se usa cerca del borde derecho.
    const caja = disparador.getBoundingClientRect();
    const ancho = panel.offsetWidth;
    const izquierdaCruda = caja.left + window.scrollX;
    const maximaIzquierda = window.scrollX + document.documentElement.clientWidth - ancho - 8;
    panel.style.top = `${caja.bottom + window.scrollY + 6}px`;
    panel.style.left = `${Math.max(window.scrollX + 8, Math.min(izquierdaCruda, maximaIzquierda))}px`;
}

// Un solo listener delegado para toda la página: los totales se repintan
// constantemente (SSE, filtros, paginación) y enganchar por elemento dejaría
// listeners huérfanos en cada refresco.
let _delegadoInstalado = false;
const _metaPorElemento = new WeakMap();

function _instalarDelegado() {
    if (_delegadoInstalado) return;
    _delegadoInstalado = true;

    document.addEventListener('click', evento => {
        const disparador = evento.target.closest(`.${CLASE_DISPARADOR}`);
        if (!disparador) {
            _cerrarPanel();
            return;
        }
        evento.preventDefault();
        evento.stopPropagation();

        if (document.getElementById(ID_PANEL) && disparador.dataset.abierto === '1') {
            disparador.dataset.abierto = '0';
            _cerrarPanel();
            return;
        }
        document.querySelectorAll(`.${CLASE_DISPARADOR}`).forEach(el => (el.dataset.abierto = '0'));
        disparador.dataset.abierto = '1';

        const enlazado = _metaPorElemento.get(disparador);
        const meta = enlazado ? enlazado.meta : _leerMetaDelDataset(disparador);
        const campo = enlazado ? enlazado.campo : disparador.dataset.desgloseCampo;
        if (hayDesgloseUtil(meta)) _abrirPanel(disparador, meta, campo);
    });

    document.addEventListener('keydown', evento => {
        if (evento.key === 'Escape') _cerrarPanel();
    });
    window.addEventListener('resize', _cerrarPanel);
    // El panel se posiciona una vez: si la página scrollea, se cierra en vez de
    // quedar flotando lejos del número que explica.
    window.addEventListener('scroll', _cerrarPanel, true);
}

function _leerMetaDelDataset(elemento) {
    if (!elemento.dataset.desgloseMeta) return null;
    try {
        return JSON.parse(elemento.dataset.desgloseMeta);
    } catch (_error) {
        return null; // dataset corrupto: sin popover, el número igual se ve
    }
}

export const DesgloseMonedaPopover = {
    /**
     * Vuelve pulsable un elemento ya renderizado.
     * @param {HTMLElement} elemento
     * @param {{meta: Object, campo: string}} opciones
     */
    enlazar(elemento, opciones) {
        if (!elemento || !opciones || !hayDesgloseUtil(opciones.meta)) return;
        _instalarDelegado();
        elemento.classList.add(CLASE_DISPARADOR);
        elemento.style.cursor = 'pointer';
        elemento.setAttribute('tabindex', '0');
        elemento.setAttribute('title', t('Ver desglose por moneda'));
        _metaPorElemento.set(elemento, { meta: opciones.meta, campo: opciones.campo });
    },

    /**
     * HTML de la marca (`≈` o el hueco `— !`) ya pulsable, para construir celdas
     * desde un template string sin tener el nodo todavía.
     * Devuelve cadena vacía si el total es exacto y mono-moneda: sin nada que explicar.
     * @param {Object} meta - `moneda_meta` del endpoint
     * @param {string} campo - clave del monto en `desglose[].montos`
     * @param {{hueco?: boolean}} [opciones] - hueco: el total es null (no se pudo consolidar)
     */
    marcaHtml(meta, campo, opciones = {}) {
        if (!hayDesgloseUtil(meta)) return '';
        const texto = marcaDelTotal(meta, opciones);
        if (!texto) return '';
        const esHueco = texto !== '≈';

        _instalarDelegado();
        const color = esHueco ? COLOR_AVISO : 'inherit';
        return `<span class="${CLASE_DISPARADOR}" data-desglose-campo="${esc(campo)}" data-desglose-meta="${esc(JSON.stringify(meta))}" tabindex="0" title="${esc(t('Ver desglose por moneda'))}" style="cursor:pointer;color:${color};text-decoration:underline dotted;text-underline-offset:2px;">${texto}</span>`;
    },

    /** Cierra el panel abierto (para llamar antes de repintar una vista). */
    cerrar: _cerrarPanel,
};
